import { loadStripe } from '@stripe/stripe-js'

// Single shared Stripe.js instance. Lazy-loaded — the SDK only fetches when
// a customer detail page mounts and asks for it.
const PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
const FUNCTIONS_BASE = import.meta.env.VITE_FUNCTIONS_BASE_URL || ''

let _stripe = null
export function getStripe() {
  if (_stripe) return _stripe
  if (!PUBLISHABLE_KEY) {
    console.warn('[stripe] VITE_STRIPE_PUBLISHABLE_KEY is missing — bank collection will not work.')
    return Promise.resolve(null)
  }
  _stripe = loadStripe(PUBLISHABLE_KEY)
  return _stripe
}

// Tiny POST helper. Throws with a readable error so the UI can surface it.
async function fnPost(path, body) {
  if (!FUNCTIONS_BASE) {
    throw new Error('Cloud Functions are not deployed yet. Upgrade the Firebase project to Blaze and run `firebase deploy --only functions`.')
  }
  const res = await fetch(`${FUNCTIONS_BASE}/${path.replace(/^\//, '')}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  })
  const text = await res.text()
  let json = null
  try { json = text ? JSON.parse(text) : null } catch {}
  if (!res.ok) {
    const msg = json?.error || text || `${path} failed (HTTP ${res.status})`
    throw new Error(msg)
  }
  return json || {}
}

// 1) Server creates a Stripe Customer (if needed) + a SetupIntent for
//    the requested payment method type. Default 'us_bank_account' keeps
//    the legacy ACH path; pass 'card' for the credit-card auto-pay
//    setup flow (Card Element). Returns its client_secret.
export function createBankSetupIntent(customerId, paymentMethodType) {
  return fnPost('createSetupIntent', { customerId, paymentMethodType: paymentMethodType || 'us_bank_account' })
}

// 2) After Stripe.js confirms the SetupIntent, the resulting payment_method
//    is bound to the Customer for future charges. Server saves the pm_…
//    ID, retrieves the PM's actual type/brand/last4/funding from Stripe,
//    and persists it all in one atomic update.
export function savePaymentMethod(customerId, paymentMethodId) {
  return fnPost('savePaymentMethod', { customerId, paymentMethodId })
}

// Fire an ACH PaymentIntent for the most recent open invoice (server picks).
export function chargeCustomer(customerId) {
  return fnPost('chargeCustomer', { customerId })
}

// Fire a charge for a specific invoice (used from /invoices and /customers/:id).
export function chargeInvoice(invoiceId) {
  return fnPost('chargeInvoice', { invoiceId })
}

// Public /pay page — fetch sanitized invoice + customer view by invoiceId.
// No auth required.
export function getInvoiceForPayment(invoiceId) {
  return fnPost('getInvoiceForPayment', { invoiceId })
}

// Public /pay page — submit raw bank details for one-shot ACH payment.
// Server tokenizes, attaches PM to the customer, and confirms a
// PaymentIntent. Returns { ok, paymentIntentId, status }.
export function submitInvoiceAchPayment(payload) {
  return fnPost('submitInvoiceAchPayment', payload)
}

// stripe_invoice flow — first-time customer. Server creates a Stripe
// PaymentIntent with us_bank_account + Financial Connections so the
// client can launch Stripe's hosted bank-login modal via
// stripe.collectBankAccountForPayment. setup_future_usage:'off_session'
// causes the resulting PM to be saved on the Customer; the webhook
// persists its id back to the WifiBilling customer record so subsequent
// invoices use the one-tap path. Returns { clientSecret, paymentIntentId,
// stripeCustomerId }.
export function createInvoicePaymentIntent(invoiceId) {
  return fnPost('createInvoicePaymentIntent', { invoiceId })
}

// stripe_invoice flow — repeat one-tap pay. Server charges the saved bank
// PM off_session for the given invoice. Returns { ok, paymentIntentId,
// status }.
export function chargeInvoiceWithSavedBank(invoiceId) {
  return fnPost('chargeInvoiceWithSavedBank', { invoiceId })
}

// auto-draft setup — SetupIntent (no charge) for the FC bank-link flow.
// Returns { clientSecret, setupIntentId, stripeCustomerId }.
export function setupInvoiceAutoDraft(invoiceId) {
  return fnPost('setupInvoiceAutoDraft', { invoiceId })
}

// auto-draft setup — after the SetupIntent succeeds, persist the PM,
// flip the customer onto the auto-draft rail, and mark THIS invoice as
// pendingFirstCharge so admin runs the first charge manually.
export function confirmInvoiceAutoDraft(invoiceId, paymentMethodId) {
  return fnPost('confirmInvoiceAutoDraft', { invoiceId, paymentMethodId })
}

// bank-on-file setup — after the SetupIntent succeeds, persist the PM and
// put the customer on the stripe_invoice rail with autoDraft:false. The
// current invoice is NOT marked pending — the customer is expected to come
// back and click Pay themselves (or wait for next month's invoice email).
export function confirmInvoiceBankInvoice(invoiceId, paymentMethodId) {
  return fnPost('confirmInvoiceBankInvoice', { invoiceId, paymentMethodId })
}

// mailing-payment setup — no Stripe, no PM. Writes paymentMethod:'mail'
// to the customer record. Mark mails a check by the due date each month.
export function confirmInvoiceMailPayment(invoiceId) {
  return fnPost('confirmInvoiceMailPayment', { invoiceId })
}

// Admin "Mark Paid" — flip invoice to paid + send slim receipt. No Stripe.
export function markInvoicePaidManual(invoiceId) {
  return fnPost('markInvoicePaidManual', { invoiceId })
}

// card_invoice / card_auto first-time payment. Server creates a Stripe PI
// with payment_method_types ['card'] + setup_future_usage:'off_session',
// returns clientSecret + the surcharged total (cardFunding=credit on
// repeat invoices) so the /pay page can show the breakdown. The client
// confirms via stripe.confirmCardPayment with the Card Element.
export function createCardPaymentIntent(invoiceId) {
  return fnPost('createCardPaymentIntent', { invoiceId })
}

// card_invoice repeat one-tap pay. Server charges the saved card off_session
// for the given invoice (with credit-card surcharge when applicable).
export function chargeInvoiceWithSavedCard(invoiceId) {
  return fnPost('chargeInvoiceWithSavedCard', { invoiceId })
}

// Persist a just-paid card to the customer record (mirrors confirmInvoiceBankInvoice).
export function confirmInvoiceCardPayment(invoiceId, paymentMethodId) {
  return fnPost('confirmInvoiceCardPayment', { invoiceId, paymentMethodId })
}


// Send the SendGrid invoice email for a specific invoice.
export function sendInvoiceEmail(invoiceId) {
  return fnPost('sendInvoiceEmail', { invoiceId })
}

// Send a past-due reminder email. stage='first' (friendly) or 'final'
// (shutoff warning). Backed by Gmail OAuth on the server.
export function sendInvoiceReminder(invoiceId, stage = 'first') {
  return fnPost('sendInvoiceReminder', { invoiceId, stage })
}

// Admin "Test Now" — pings the Resend API with the saved key. Writes
// settings/emailHealth so the dashboard banner + settings indicator update
// via the watcher. Returns { status, checkedAt, error? }.
export function verifyResendConnection() {
  return fnPost('verifyResendConnection', {})
}

// ── SMS (Android SMS Gateway · sms-gate.app) ──────────────────────────────
// Server reads each customer's phone, substitutes [Name] / [amount] /
// [link] in the template, relays through the registered Android device,
// and writes /smsLog/{key}.
export function sendSmsToCustomer(customerId, message) {
  return fnPost('sendSmsGateway', { customerId, message })
}
export function sendSmsBulk(customerIds, messageTemplate) {
  return fnPost('sendSmsGatewayBulk', { customerIds, messageTemplate })
}
